/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import './TemplateInstanceChangeStatusDialog.scss';

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';

import {
    changeStatusDialogDataVar,
    statusChangedVar,
} from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { LogCategory, PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../logger';
import { ApprovalTemplateInstance, UpdateApprovalTemplateInstanceClient } from '../../../service/gen/templates';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { stringFormat } from '../../../utilities/Utils';
import { ConfirmationDialog, ConfirmationDialogProps } from '../../common/ConfirmationDialog';
import { LoadingPage } from '../../containers/LoadingPage';

export const TemplateInstanceChangeStatusDialogContainer: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const dialogData = useReactiveVar(changeStatusDialogDataVar);
    const loc = host.getLocale();
    const logger = host.getLogger();
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    let title;
    let content = stringFormat(loc.strings.disable_template_dialog_content, {
        templateInstanceTitle: dialogData.templateInstanceTitle,
    });
    let primaryActionText = loc.strings.disable_text;

    if (dialogData.targetStatus) {
        title = loc.strings.enable_template_dialog_title;
        content = stringFormat(loc.strings.enable_template_dialog_content, {
            templateInstanceTitle: dialogData.templateInstanceTitle,
        });
        primaryActionText = loc.strings.enable_text;
    } else {
        title = loc.strings.disable_template_dialog_title;
        content = stringFormat(loc.strings.disable_template_dialog_content, {
            templateInstanceTitle: dialogData.templateInstanceTitle,
        });
        primaryActionText = loc.strings.disable_text;
    }

    const onCancelClick = (): void => {
        logger.markScenario(ScenarioName.TemplateManagement, StepName.ExitChangeStatusModule, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.TemplateManagement,
            StepName.ExitChangeStatusModule,
            'click',
            'nav',
            'actionButton',
            'cancelButton',
            PanelRegion.Main,
            PanelType.ChangeTemplateStatus
        );

        changeStatusDialogDataVar({ ...dialogData, isOpen: false });
    };

    const onDisableClick = (): void => {
        setIsLoading(true);
        const startTime = performance.now();
        logger.logTraceInfo(
            LogCategory.TemplateManagement,
            `${dialogData.targetStatus ? 'Enabling the template instance' : 'Disabling the template instance'}.`
        );
        logger.markScenario(ScenarioName.TemplateManagement, StepName.UpdateStatusButtonPressed, StatusName.NA, {
            data: {
                templateInstanceId: dialogData.templateInstanceId,
                templateInstanceStatus: dialogData.targetStatus,
            },
        });
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.TemplateManagement,
            StepName.UpdateStatusButtonPressed,
            'click',
            'submit',
            'PrimaryButton',
            'updateStatusButton',
            PanelRegion.Main,
            PanelType.ChangeTemplateStatus,
            {
                templateInstanceStatus: dialogData.targetStatus,
                templateInstanceId: dialogData.templateInstanceId,
            }
        );

        new UpdateApprovalTemplateInstanceClient()
            .updateApprovalTemplateInstance({
                Id: dialogData.templateInstanceId,
                IsEnabled: dialogData.targetStatus,
            })
            .then((result: ApprovalTemplateInstance): void => {
                logger.logTraceInfo(
                    LogCategory.TemplateManagement,
                    `Template instance is successfully ${result.IsEnabled ? 'enabled' : 'disabled'}.`
                );
                logger.markScenario(
                    ScenarioName.TemplateManagement,
                    StepName.UpdateStatusButtonPressed,
                    StatusName.Success,
                    {
                        stepElapsed: performance.now() - startTime,
                        data: {
                            templateInstanceStatus: dialogData.targetStatus,
                            templateInstanceId: dialogData.templateInstanceId,
                        },
                    }
                );
                logger.markScenario(ScenarioName.TemplateManagement, StepName.ExitChangeStatusModule, StatusName.NA);
                logger.logUserBiAction(
                    SubWorkLoad.AppInteraction,
                    ScenarioName.TemplateManagement,
                    StepName.ExitChangeStatusModule,
                    'click',
                    'nav',
                    'actionButton',
                    'cancelButton',
                    PanelRegion.Main,
                    PanelType.TemplateManagement
                );

                changeStatusDialogDataVar({ ...dialogData, isOpen: false, targetStatus: result.IsEnabled });
                statusChangedVar({
                    templateInstanceId: result.Id,
                    status: result.IsEnabled,
                });
                setError('');
            })
            .catch((error: any): any => {
                setIsLoading(false);
                logger.markScenario(
                    ScenarioName.TemplateManagement,
                    StepName.UpdateStatusButtonPressed,
                    StatusName.Fail,
                    {
                        stepElapsed: performance.now() - startTime,
                        data: {
                            templateInstanceStatus: dialogData.targetStatus,
                            templateInstanceId: dialogData.templateInstanceId,
                        },
                    }
                );
                logger.logTraceHttpException('updateApprovalTemplateInstance', error?.message, error?.status);
                setError(loc.strings.error_default);
            });
    };

    const dialogProps: ConfirmationDialogProps = {
        isOpen: dialogData.isOpen,
        title: !isLoading && title,
        content: isLoading ? (
            <LoadingPage message={loc.strings.updating_template_status} />
        ) : (
            <div className='template-instance-change-status-dialog-content' aria-hidden>
                {error || content}
            </div>
        ),
        dismiss: onCancelClick,
        actions: !isLoading && [
            {
                text: loc.strings.action_cancel,
                onClick: onCancelClick,
            },
            {
                text: primaryActionText,
                onClick: onDisableClick,
                primary: true,
                disabled: isLoading,
            },
        ],
        showDarkOverlay: true,
    };

    React.useEffect((): void => {
        if (dialogData?.isOpen) {
            setError('');
            setIsLoading(false);
        }
    }, [dialogData?.isOpen]);

    return <ConfirmationDialog {...dialogProps} />;
};
